import React from 'react';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Our Story', to: '/our-story/ifys-story' },
  { label: 'Sanctuary' },
];

const HERO_LINKS = [
  { label: 'Our Pillars', image: '/images/buttons/our-pillars.png', href: '#four-pillars' },
  { label: 'Our Commitments', image: '/images/buttons/our-commitments.png', href: '#eight-commitments' },
];

const COMMITMENTS = [
  { title: 'Non-Violence', description: 'We are respectful with our words and actions.' },
  { title: 'Open Communication', description: 'We share information with honesty and sensitivity.' },
  { title: 'Emotional Intelligence', description: 'We are aware of our own feelings and the feelings of others.' },
  { title: 'Social Responsibility', description: 'We are responsible for our own behaviour towards the common good.' },
  { title: 'Social Learning', description: 'We learn from each other.' },
  { title: 'Growth & Change', description: 'We acknowledge loss and create hope for the future.' },
  { title: 'Democracy', description: 'We value the voices and views of each other.' },
  { title: 'Cultural Humility', description: 'We value and acknowledge individual diversity.' },
];

const TOOLS = [
  { title: 'Community Meetings', description: 'Short ‘check ins’ to recognise feelings, goals and support.' },
  { title: 'Go-To Plans', description: 'A list of activities that we do to look after our emotions and wellbeing.' },
  { title: 'Self-Care', description: 'A plan for how we look after our mental, physical, emotional and cultural wellbeing.' },
  { title: 'S.E.L.F', description: 'A tool for reflection, solving problems and reaching goals.' },
];

const RESOURCES = [
  {
    heading: 'Go-To Plans',
    items: [
      { label: 'Go-To Plan – Under 12', href: '/documents/Sanctuary_Go-To_Plan_Under_12.pdf' },
      { label: 'Go-To Plan – Template', href: '/documents/Sanctuary_Go-To_Plan_Template.pdf' },
      { label: 'Go-To Plan – Mandala', href: '/documents/Sanctuary_Go-To_Plan_Mandala.pdf' },
      { label: 'Go-To Plan – Hand Template', href: '/documents/Sanctuary_Go-To_Plan_Hand.pdf' },
      { label: 'Go-To Plan – Activity', href: '/documents/Sanctuary_Go-To_Plan_Activity.pdf' },
    ],
  },
  {
    heading: 'Self Care',
    items: [
      { label: 'Sanctuary Self Care Reflection', href: '/documents/Sanctuary_Self_Care_Reflection_2026.pdf' },
      { label: 'Personal Self Care Plan', href: '/documents/Sanctuary_Personal_Self_Care_Plan_2026.pdf' },
    ],
  },
];

const PILLARS = [
  {
    title: 'Theory',
    description:
      'As a trauma-informed model, Sanctuary provides education and tools to support staff and service users to understand how trauma, stress and adversity can influence behaviour.',
  },
  {
    title: 'S.E.L.F framework',
    description:
      'Safety, Emotion Management, Loss and Future. The S.E.L.F. acronym is the simple structure we use to ensure a shared language throughout the organisation, so that all people can participate with understanding to address common barriers to change, social responsibility and open communication.',
  },
  {
    title: 'Eight commitments',
    description:
      'The eight commitments are agreements we make as an organisation to guide our interactions with colleagues, service users and the wider community to create safety and opportunity for growth and change.',
  },
  {
    title: 'Sanctuary tool kit',
    description:
      'To create a trauma-informed culture, the Sanctuary tool kit provides us with a range of practical skills and resources that staff and service users can use to overcome challenges and help identify areas for growth.',
  },
];

const HeroLink = ({ label, image, href, size }) => (
  <a
    href={href}
    aria-label={`Jump to ${label}`}
    className="inline-block rounded-full transition-transform duration-200 hover:scale-[1.04] focus:outline-none focus-visible:ring-4 focus-visible:ring-white/70"
  >
    <img src={image} alt={label} className={`block select-none ${size}`} draggable={false} />
  </a>
);

const SectionHeader = ({ title, intro }) => (
  <div className="max-w-3xl mb-10 sm:mb-12">
    <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue tracking-tight">{title}</h3>
    {intro && <p className="mt-2 text-base sm:text-lg text-gray-600 leading-relaxed">{intro}</p>}
  </div>
);

const PdfIcon = () => (
  <svg
    aria-hidden="true"
    className="w-5 h-5 flex-shrink-0 text-ifys-blue"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.75}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  </svg>
);

const SanctuaryPage = () => {
  return (
    <PageLayout activeLink="our-story">
      <PageHero
        title="Sanctuary"
        backgroundImage="/images/thumb3.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      >
        {HERO_LINKS.map((link) => (
          <HeroLink key={link.label} {...link} size="w-20 h-20 lg:w-24 lg:h-24 xl:w-28 xl:h-28" />
        ))}
      </PageHero>

      <section className="md:hidden bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex flex-wrap items-center gap-4 sm:gap-5">
          {HERO_LINKS.map((link) => (
            <HeroLink key={link.label} {...link} size="w-20 h-20 sm:w-24 sm:h-24" />
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-ifys-blue tracking-tight leading-tight">
            Sanctuary at IFYS
          </h2>
          <div className="mt-8 pt-8 border-t border-gray-200 space-y-5 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              At IFYS, we use the <strong className="text-ifys-blue">Sanctuary Model</strong> to help
              create a safe environment, for staff and those we work with, where everyone feels heard
              and respected.
            </p>
            <p>
              Sanctuary can help us recognise our own feelings and needs, support each other, and
              create hope for the future. This approach is built on eight key commitments that guide
              how we work together every day.
            </p>
          </div>
        </div>
      </section>

      <section id="eight-commitments" className="bg-ifys-light-gray border-y border-gray-200 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeader title="Our eight commitments" />
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {COMMITMENTS.map(({ title, description }) => (
              <li key={title} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <h4 className="text-base sm:text-lg font-bold text-ifys-blue mb-2 leading-tight">
                  {title}
                </h4>
                <p className="text-sm sm:text-[15px] text-gray-600 leading-relaxed">{description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeader
            title="What does this mean for IFYS staff, service users and stakeholders?"
            intro="You may hear IFYS staff talk about Sanctuary and some of the tools, such as:"
          />
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {TOOLS.map(({ title, description }) => (
              <div key={title} className="border-l-4 border-ifys-blue pl-5 sm:pl-6 py-1">
                <dt className="text-base sm:text-lg font-bold text-ifys-blue leading-tight">{title}</dt>
                <dd className="mt-1.5 text-sm sm:text-[15px] text-gray-600 leading-relaxed">
                  {description}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-10 text-base sm:text-lg text-gray-700 leading-relaxed">
            You might find these tools useful. If you&rsquo;d like to know more, just ask.
          </p>
        </div>
      </section>

      <section id="resources" className="bg-ifys-light-gray border-y border-gray-200 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeader
            title="Sanctuary resources"
            intro="To learn more about some of these tools, you can view them below."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {RESOURCES.map(({ heading, items }) => (
              <div key={heading}>
                <h4 className="text-lg font-bold text-ifys-blue mb-4">{heading}</h4>
                <ul className="space-y-3">
                  {items.map(({ label, href }) => (
                    <li key={href}>
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${label} (PDF, opens in a new tab)`}
                        className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-5 py-4 text-sm sm:text-base font-medium text-gray-800 transition-colors hover:border-ifys-blue hover:text-ifys-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-ifys-blue"
                      >
                        <PdfIcon />
                        <span className="flex-1">{label}</span>
                        <span className="text-xs font-semibold tracking-wide text-gray-400">PDF</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="four-pillars" className="bg-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeader
            title="The Four Pillars"
            intro="The Sanctuary Model is built upon Four Pillars which guide how we work together every day."
          />
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {PILLARS.map(({ title, description }) => (
              <div key={title} className="border-l-4 border-ifys-blue pl-5 sm:pl-6 py-1">
                <dt className="text-base sm:text-lg font-bold text-ifys-blue leading-tight">{title}</dt>
                <dd className="mt-1.5 text-sm sm:text-[15px] text-gray-600 leading-relaxed">
                  {description}
                </dd>
              </div>
            ))}
          </dl>
          <img
            src="/images/sanctuary-four-pillars.png"
            alt="The Four Pillars of Sanctuary: Theory, SELF, Norms/Values and Tools"
            className="mt-12 w-full max-w-5xl mx-auto rounded-2xl shadow-sm"
            loading="lazy"
          />
        </div>
      </section>
    </PageLayout>
  );
};

export default SanctuaryPage;
